<template>
  <router-view />
  <iframe
    v-if="!isKioskRoute"
    id="ulaIframe"
    title="ulaIframe"
    name="ulaIframe"
    sandbox="allow-scripts allow-same-origin allow-storage-access-by-user-activation"
    :src="`${ulaURL}/?organization=${tenant.toUpperCase()}&application=HUB`"
    :style="'right: 0; position: absolute; width: 360px; display: none'"
  ></iframe>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';

import enumsModule from '@/store/modules/EnumsModule';
import { useDarkMode } from '@/composables/useDarkMode';
import { getConfig } from '@/config';
import { useAuthentication } from '@/composables/auth/useAuthentication';
import { UlaEvent } from '@/types/ula';
import { useRoute } from 'vue-router';
import { KIOSK_PATH_PREFIX } from '@/constants';

/*****************************************************************************
 * SETUP
 ****************************************************************************/
const { loadDarkModeFromStorage } = useDarkMode();
const { initLoggedUser, logout, refreshUser, initialized } = useAuthentication();
const route = useRoute();
const ulaURL = getConfig().ULA_URL;
const tenant = getConfig().TENANT;

enum ULA_MESSAGES {
  ULA_USER_IDENTITY = 'ula-user-identity',
  ULA_LOGOUT = 'ula-logout',
  ULA_ID_TOKEN = 'ula-id-token',
}

loadDarkModeFromStorage();

const isPublic = computed(() => !!route.meta?.isPublicRoute);
const isKioskRoute = computed(() => route.path.startsWith(KIOSK_PATH_PREFIX));

onMounted(async () => {
  if (isKioskRoute.value) {
    // Kiosk routes auth via service-account header (see useKioskInterceptor);
    // ULA-based interactive login is intentionally skipped.
    return;
  }
  globalThis.onmessage = async (e: MessageEvent<UlaEvent>): Promise<void> => {
    if (isPublic.value) {
      return;
    } else if (e.origin === ulaURL) {
      console.log(`📨 message from ULA iframe: ${e.data.name}`, e.data);
    } else {
      return;
    }

    switch (e.data.name) {
      case ULA_MESSAGES.ULA_USER_IDENTITY: {
        if (initialized.value) {
          await refreshUser(e.data.user);
        } else {
          initLoggedUser(e.data.user);
        }

        await enumsModule.fetchEnums();
        break;
      }
      case ULA_MESSAGES.ULA_LOGOUT: {
        await logout(false);
        break;
      }
      case ULA_MESSAGES.ULA_ID_TOKEN:
        break; // handled as promise based communication in useUla composable
      default: {
        console.error('Unknown message from ULA iframe: ', e.data);
      }
    }
  };
});
</script>
